import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {mapCollectionItemResponseInstance} from '@toolify/client/src/modules/api/singletons/mapCollectionItemResponseInstance'
import {
  IGetCollectionItemsRequestInterfaces,
} from '@toolify/server/src/controllers/CollectionController/validators/GetCollectionItems/types/IGetCollectionItemsRequestInterfaces'
import {
  ICreateCollectionItemRequestInterfaces,
} from '@toolify/server/src/controllers/CollectionController/validators/CreateCollectionItem/types/ICreateCollectionItemRequestInterfaces'
import {
  IUpdateCollectionItemRequestInterfaces,
} from '@toolify/server/src/controllers/CollectionController/validators/UpdateCollectionItem/types/IUpdateCollectionItemRequestInterfaces'
import {
  IDeleteCollectionItemRequestInterfaces,
} from '@toolify/server/src/controllers/CollectionController/validators/DeleteCollectionItem/types/IDeleteCollectionItemRequestInterfaces'
import {
  IUpdateCollectionItemMembershipRequestInterfaces,
} from '@toolify/server/src/controllers/CollectionController/validators/UpdateCollectionItemMembership/types/IUpdateCollectionItemMembershipRequestInterfaces'
import {CollectionItemType} from '@toolify/server/src/models/mongoose/CollectionItemModel/enum/CollectionItemType'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {
  ICollectionItemTypeStrategyInterfacesMap,
} from '@toolify/server/src/services/CollectionService/types/ICollectionItemTypeStrategyInterfacesMap'

export const useCollectionApiStore = defineStore('collectionApi', {
  actions: {
    async getCollectionItems(workspaceId: string): Promise<IGetCollectionItemsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {collectionItems} = await apiRequestStore.get<IGetCollectionItemsRequestInterfaces>(
        `/workspace/${workspaceId}/collection-items`,
      )
      return {
        collectionItems: collectionItems.map(mapCollectionItemResponseInstance),
      }
    },

    async createCollectionItem(
      workspaceId: string,
      body: {
        collectionItemType: CollectionItemType
        collectionItemTypePayload: ICollectionItemTypeStrategyInterfacesMap[CollectionItemType]['payload']
        name?: string | null
        description?: string | null
        parentCollectionId?: string | null
        position?: number
      },
    ): Promise<ICreateCollectionItemRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {collectionItem} = await apiRequestStore.post<ICreateCollectionItemRequestInterfaces>(
        `/workspace/${workspaceId}/collection-item`,
        {data: body},
      )
      return {
        collectionItem: mapCollectionItemResponseInstance(collectionItem),
      }
    },

    async updateCollectionItem(
      collectionItemId: string,
      body: IUpdateCollectionItemRequestInterfaces['body'],
    ): Promise<IUpdateCollectionItemRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {collectionItem} = await apiRequestStore.put<IUpdateCollectionItemRequestInterfaces>(
        `/collection-item/${collectionItemId}`,
        {data: body},
      )
      return {
        collectionItem: mapCollectionItemResponseInstance(collectionItem),
      }
    },

    async deleteCollectionItem(collectionItemId: string): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.delete<IDeleteCollectionItemRequestInterfaces>(`/collection-item/${collectionItemId}`)
    },

    async updateCollectionItemMembership(
      workspaceId: string,
      body: {
        parentCollectionId: string | null
        resourceType: ResourceType
        resourceId: string
        isMember: boolean
        position?: number
      },
    ): Promise<IUpdateCollectionItemMembershipRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {collectionItem} = await apiRequestStore.put<IUpdateCollectionItemMembershipRequestInterfaces>(
        `/workspace/${workspaceId}/collection-item/membership`,
        {data: body},
      )
      return {
        collectionItem: collectionItem ? mapCollectionItemResponseInstance(collectionItem) : null,
      }
    },
  },
})
